import { useEffect, useMemo, useState } from 'react';
import { useAiAccess } from './ai/useAiAccess.js';
import { useCard41Access } from './admin/card41/useCard41Access.js';
import {
  Badge, Box, Button, Group, Paper, SimpleGrid, Stack, Text, TextInput, UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { useAuth, isClientUser } from '../auth/AuthContext.js';
import { manualCounterpartiesApi } from '../api/manual-counterparties.js';
import { ClientHome } from './client/home/ClientHome.js';
import { sectionIcon, adminSectionIcon, sectionTone } from '../components/internal/sectionIcons.js';
import { InboxCheckIcon } from '../components/internal/internalIcons.js';
import { ChevronRightIcon, SearchIcon } from '../components/client/clientIcons.js';
import {
  visibleWorkSections, visibleAdminSections, canSeePendingCounterparties, matchesQuery,
  sectionsOfGroup, GROUP_LABEL,
  type Section, type AdminSection,
} from '../navigation/sections.js';
import {
  BORDER, CONTENT_MAX_WIDTH, DESKTOP_QUERY, MUTED,
  SHADOW_CARD, SHADOW_CARD_HOVER, SURFACE, TEXT, WARN,
} from '../theme/tokens.js';

/**
 * Рабочий стол внутреннего портала.
 *
 * Состав разделов и правила видимости живут в `navigation/sections.ts` — тот же
 * источник, что у сайдбара, поэтому меню и плитки не могут разъехаться.
 * Права здесь только отображаются: серверная авторизация остаётся источником истины.
 */

/** Крупная карточка рабочего раздела. */
function WorkCard({
  to, label, description, icon, tone, badge,
}: {
  to: string; label: string; description: string;
  icon: React.ReactNode; tone: { fg: string; bg: string }; badge?: number;
}) {
  return (
    <UnstyledButton
      component={Link}
      to={to}
      style={{
        display: 'block', background: SURFACE, borderRadius: 16,
        border: `1px solid ${BORDER}`, boxShadow: SHADOW_CARD,
        padding: 18, height: '100%',
        transition: 'box-shadow 120ms ease, transform 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = SHADOW_CARD_HOVER;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = SHADOW_CARD;
        e.currentTarget.style.transform = 'none';
      }}
    >
      <Group gap={14} wrap="nowrap" align="flex-start">
        <Box
          style={{
            width: 46, height: 46, borderRadius: 12, background: tone.bg, color: tone.fg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={8} wrap="nowrap">
            <Text fz={16} fw={600} c={TEXT} style={{ minWidth: 0 }}>{label}</Text>
            {badge != null && badge > 0 && (
              <Badge size="sm" variant="filled" color="orange" style={{ flexShrink: 0 }}>{badge}</Badge>
            )}
          </Group>
          <Text fz={13.5} c={MUTED} mt={3}>{description}</Text>
        </Box>
        <Box style={{ color: '#B6C2D0', display: 'flex', flexShrink: 0, marginTop: 12 }}>
          <ChevronRightIcon size={18} />
        </Box>
      </Group>
    </UnstyledButton>
  );
}

/** Компактная карточка справочника. */
function AdminCard({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) {
  return (
    <UnstyledButton
      component={Link}
      to={to}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: SURFACE, borderRadius: 14, border: `1px solid ${BORDER}`,
        boxShadow: SHADOW_CARD, padding: '14px 16px', minHeight: 56,
        transition: 'box-shadow 120ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = SHADOW_CARD_HOVER; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = SHADOW_CARD; }}
    >
      <Box style={{ color: MUTED, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Text fz={14.5} fw={500} c={TEXT} style={{ flex: 1, minWidth: 0 }}>{label}</Text>
      <Box style={{ color: '#B6C2D0', display: 'flex', flexShrink: 0 }}>
        <ChevronRightIcon size={16} />
      </Box>
    </UnstyledButton>
  );
}

export function HomePage() {
  const { state } = useAuth();
  const isDesktop = useMediaQuery(DESKTOP_QUERY, false, { getInitialValueInEffect: false });
  // Роль вычисляем безопасно (до раннего return) — хуки ниже должны вызываться безусловно.
  const role = state.status === 'authed' ? state.user.role : null;
  const [pendingCp, setPendingCp] = useState(0);
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Счётчик заявок на контрагентов — только админ согласовывает.
    if (role !== 'admin' && role !== 'genadmin') return;
    manualCounterpartiesApi.listPending()
      .then((rows) => setPendingCp(rows.length))
      .catch(() => { /* не критично */ });
  }, [role]);

  const aiEnabled = useAiAccess(role);
  const card41Enabled = useCard41Access(role);
  const work: Section[] = useMemo(() => (role ? visibleWorkSections(role, aiEnabled, card41Enabled) : []), [role, aiEnabled, card41Enabled]);
  const admin: AdminSection[] = useMemo(() => (role ? visibleAdminSections(role) : []), [role]);

  // Поиск фильтрует ТОЛЬКО уже доступные пользователю разделы — скрытое по правам
  // через него не всплывает.
  const workShown = useMemo(() => work.filter((s) => matchesQuery(s, query)), [work, query]);
  const adminShown = useMemo(() => admin.filter((s) => matchesQuery(s, query)), [admin, query]);
  const showPending = role != null && canSeePendingCounterparties(role, pendingCp)
    && matchesQuery({ label: 'Заявки на контрагентов', description: 'Согласование новых контрагентов' }, query);

  if (state.status !== 'authed') return null;
  if (isClientUser(state.user)) return <ClientHome />;
  const u = state.user;
  const nothingFound = workShown.length === 0 && adminShown.length === 0 && !showPending;

  return (
    <Box px={isDesktop ? 28 : 16} py={isDesktop ? 24 : 18}>
      <Box style={{ maxWidth: CONTENT_MAX_WIDTH, marginInline: 'auto' }}>
        <Stack gap={isDesktop ? 24 : 18}>
          <Box>
            <Text component="h1" fz={isDesktop ? 30 : 24} fw={700} c={TEXT} lh={1.2} m={0}>
              Рабочий стол
            </Text>
          </Box>

          {/* Приветствие + поиск. Блок с текущей/назначенными ролями убран по
              фидбеку Artem 07.08.2026 — роль пользователю и так известна. */}
          <Group justify="space-between" align="center" gap={16} wrap="wrap">
            <Stack gap={2}>
              <Text fz={16.5} fw={600} c={TEXT}>Здравствуйте, {u.fullName}</Text>
              <Text fz={14} c={MUTED}>Выберите раздел для работы</Text>
            </Stack>

            <TextInput
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Поиск по разделам"
              aria-label="Поиск по разделам"
              size="md"
              leftSection={<Box style={{ color: MUTED, display: 'flex' }}><SearchIcon size={18} /></Box>}
              styles={{ input: { borderColor: BORDER, background: SURFACE } }}
              style={{ width: isDesktop ? 380 : '100%' }}
            />
          </Group>

          {nothingFound ? (
            <Paper p={32} style={{ background: SURFACE, border: `1px solid ${BORDER}`, boxShadow: SHADOW_CARD }}>
              <Stack gap={10} align="center">
                <Box style={{ color: '#B6C2D0' }}><SearchIcon size={26} /></Box>
                <Text fz={15.5} fw={600} c={TEXT}>Ничего не найдено</Text>
                <Text fz={14} c={MUTED} ta="center">
                  По запросу «{query.trim()}» нет доступных вам разделов.
                </Text>
                <Button variant="light" color="eurasiaTeal" onClick={() => setQuery('')}>
                  Сбросить поиск
                </Button>
              </Stack>
            </Paper>
          ) : (
            <>
              {/*
                Плитки сгруппированы так же, как меню (sections.ts): новости,
                работа, аналитика. Раньше здесь был один блок «Рабочие
                процессы» на всё сразу, и он расходился по смыслу с сайдбаром.
              */}
              {(['news', 'work', 'analytics'] as const).map((g) => {
                const rows = sectionsOfGroup(workShown, g);
                const withPending = g === 'work' && showPending;
                if (rows.length === 0 && !withPending) return null;
                return (
                  <Box component="section" key={g}>
                    <Text component="h2" fz={isDesktop ? 20 : 18} fw={700} c={TEXT} mb={12} mt={0}>
                      {GROUP_LABEL[g]}
                    </Text>
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing={isDesktop ? 16 : 12}>
                      {rows.map((s) => (
                        <WorkCard
                          key={s.id}
                          to={s.to}
                          label={s.label}
                          description={s.description}
                          icon={sectionIcon(s.id, 22)}
                          tone={sectionTone(s.id)}
                        />
                      ))}
                      {withPending && (
                        <WorkCard
                          to="/admin/pending-counterparties"
                          label="Заявки на контрагентов"
                          description="Согласование новых контрагентов"
                          icon={<InboxCheckIcon size={22} />}
                          tone={{ fg: WARN, bg: '#FEF3E2' }}
                          badge={pendingCp}
                        />
                      )}
                    </SimpleGrid>
                  </Box>
                );
              })}

              {adminShown.length > 0 && (
                <Box component="section">
                  <Group gap={10} mb={12} wrap="wrap">
                    <Text component="h2" fz={isDesktop ? 20 : 18} fw={700} c={TEXT} m={0}>
                      Справочники
                    </Text>
                    <Badge size="sm" variant="light" color="eurasiaTeal">admin</Badge>
                    <Text fz={13.5} c={MUTED} visibleFrom="sm">
                      Доступно пользователям с административными правами
                    </Text>
                  </Group>
                  <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 4 }} spacing={isDesktop ? 14 : 10}>
                    {adminShown.map((s) => (
                      <AdminCard
                        key={s.id}
                        to={s.to}
                        label={s.label}
                        icon={adminSectionIcon(s.id, 20)}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
